// Service worker registration with defensive measures:
// 1. Auto-skipWaiting + reload on new SW (no stale SW)
// 2. Health check heartbeat (detect wedged SW, self-heal)
// 3. Auth token relay for SW image requests

import { auth } from './config/firebase';

const SW_TAG = '[CSAI-SW]';

/** How often to ping the SW to check if it's alive. */
const HEALTH_CHECK_INTERVAL_MS = 30000;
/** How long to wait for a health check response before declaring the SW wedged. */
const HEALTH_CHECK_TIMEOUT_MS = 3000;

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

export function register(config?: Config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href);
    if (publicUrl.origin !== window.location.origin) return;

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;
      if (isLocalhost) {
        checkValidServiceWorker(swUrl, config);
      } else {
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      // Start health check heartbeat once SW is registered
      startHealthCheck();

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) return;

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // New SW installed while old one is active — tell it to take over immediately
              console.log(`${SW_TAG} New service worker installed — sending SKIP_WAITING`);
              if (registration.waiting) {
                registration.waiting.postMessage({ type: 'SKIP_WAITING' });
              }
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // First-time install — no reload needed
              console.log(`${SW_TAG} Service worker installed for the first time`);
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error(`${SW_TAG} Service worker registration failed:`, error);
    });
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.');
    });
}

// ---------------------------------------------------------------------------
// #1: Auto-reload when a new SW takes control
// ---------------------------------------------------------------------------
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    console.log(`${SW_TAG} New service worker activated — reloading page`);
    window.location.reload();
  });
}

// ---------------------------------------------------------------------------
// #2: Health check heartbeat — detect wedged SW and self-heal
// ---------------------------------------------------------------------------
function startHealthCheck() {
  setInterval(() => {
    const controller = navigator.serviceWorker?.controller;
    if (!controller) return; // No SW controlling — nothing to check

    const messageChannel = new MessageChannel();
    let responded = false;

    const timeout = setTimeout(() => {
      if (!responded) {
        console.error(`${SW_TAG} Health check FAILED — SW did not respond within ${HEALTH_CHECK_TIMEOUT_MS}ms. Unregistering and reloading.`);
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      }
    }, HEALTH_CHECK_TIMEOUT_MS);

    messageChannel.port1.onmessage = (event) => {
      if (event.data?.type === 'HEALTH_CHECK_OK') {
        responded = true;
        clearTimeout(timeout);
      }
    };

    controller.postMessage({ type: 'HEALTH_CHECK' }, [messageChannel.port2]);
  }, HEALTH_CHECK_INTERVAL_MS);
}

// ---------------------------------------------------------------------------
// Auth token relay: SW requests Firebase tokens for image auth
// ---------------------------------------------------------------------------
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', async (event) => {
    if (event.data && event.data.type === 'GET_AUTH_TOKEN') {
      const port = event.ports[0];
      if (port) {
        try {
          const user = auth.currentUser;
          const token = user ? await user.getIdToken() : null;
          port.postMessage({ token });
        } catch {
          console.error(`${SW_TAG} Failed to get Firebase auth token for SW`);
          port.postMessage({ token: null });
        }
      }
    }
  });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}
